import type {
  AutomationSummary,
  BriefSummary,
  ChatMessage,
  FileStoreFile,
  LogEntry,
  LogSource,
  RunStatus,
  Settings,
} from "./types";

// Called when any protected request comes back 401 (e.g. session expired) so the
// app can drop back to the login screen. Registered by App.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when we actually send a body — otherwise a
  // bodyless POST (e.g. /api/run) trips Fastify's "empty JSON body" 400.
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
  if (init?.body != null && !("Content-Type" in headers)) headers["Content-Type"] = "application/json";
  const res = await fetch(url, { ...init, headers });
  const text = await res.text();
  const body = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    if (res.status === 401 && body?.authRequired) onUnauthorized?.();
    const message = body?.error ?? `Request failed: ${res.status}`;
    const err = new Error(message) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}

export interface AuthState {
  enabled: boolean;
  authenticated: boolean;
  user: string | null;
}

export const api = {
  authStatus: () => req<AuthState>("/api/auth"),

  login: (username: string, password: string) =>
    req<{ ok: boolean; user: string | null }>("/api/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  logout: () => req<{ ok: boolean }>("/api/logout", { method: "POST" }),

  listBriefs: () => req<{ briefs: BriefSummary[] }>("/api/briefs").then((r) => r.briefs),

  briefContent: (id: string) =>
    req<{ id: string; content: string }>(`/api/briefs/${encodeURIComponent(id)}/content`).then((r) => r.content),

  chat: (payload: {
    messages: { role: ChatMessage["role"]; content: string }[];
    currentBriefId?: string | null;
    otherBriefIds?: string[];
    conversationId?: string | null;
  }) =>
    req<{ reply: string; conversationId?: string; citations?: unknown[]; finishReason?: string }>("/api/chat", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  runNow: () => req<{ ok: boolean; status: string; message: string }>("/api/run", { method: "POST" }),

  // Fire-and-forget trigger; progress is observed via runStatus polling.
  triggerRun: () => req<{ accepted: boolean }>("/api/run?wait=false", { method: "POST" }),

  runStatus: () => req<RunStatus>("/api/run/status"),

  getSettings: () => req<Settings>("/api/settings"),

  saveSettings: (patch: Partial<Settings>) =>
    req<Settings>("/api/settings", { method: "PUT", body: JSON.stringify(patch) }),

  getInstructions: () =>
    req<{ exists: boolean; id?: string; content: string }>("/api/instructions"),

  saveInstructions: (content: string) =>
    req<{ exists: boolean; id?: string; content: string }>("/api/instructions", {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),

  listAutomations: () =>
    req<{ automations: AutomationSummary[] }>("/api/automations").then((r) => r.automations),

  listFilestore: () =>
    req<{ files: FileStoreFile[] }>("/api/filestore").then((r) => r.files),

  filestoreContent: (name: string) =>
    req<{ name: string; content: string; contentType: string | null }>(
      `/api/filestore/content?name=${encodeURIComponent(name)}`,
    ),

  deleteFilestore: (name: string) =>
    req<{ name: string; deleted: boolean }>(`/api/filestore?name=${encodeURIComponent(name)}`, {
      method: "DELETE",
    }),

  getLogs: (sources: LogSource[], since?: string) => {
    const params = new URLSearchParams();
    if (sources.length) params.set("source", sources.join(","));
    if (since) params.set("since", since);
    const qs = params.toString();
    return req<{ entries: LogEntry[] }>(`/api/logs${qs ? `?${qs}` : ""}`).then((r) => r.entries);
  },

  postEvent: (message: string, level: "info" | "warn" | "error" = "info", detail?: unknown) =>
    fetch("/api/logs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, level, detail }),
    }).catch(() => {}),
};
